

import { Action, Getter, namespace } from "vuex-class"
import { Component, Vue, Watch } from "vue-property-decorator"

// @ts-ignore
import vSelect from "vue-select"
import VuePerfectScrollbar from "vue-perfect-scrollbar"

import DataTable from "@/components/Config/DataTable.vue"
import FieldControl from "@/components/Fields/FieldControl.vue"
import SkeletonLoader from "@/components/SkeletonLoader.vue"

import api from "@/services/api"
import apiTenant from "@/services/api_Tenant"
import { CurrentUser } from "@/types/user"
import Guid from "@/scripts/guid"
import { insightDuplicationOptions } from "@/types/insights/insightDuplication"
import { TenantSettingKeys, SettingValuePair } from "@/types/tenantSetting"
import { TenantFeatures } from "@/types/tenantFeatures"
import TimezoneAliases from "@/components/Config/TenantSettings/TimezoneAliases.vue"
import { TriageTemplateChangeSettingOptions } from "@/types/triage"
import { Field } from "@/types"
import { CaseColumn } from "@/types/caseColumns"
import { reassignmentAuditSupportedBuiltInCaseColumnKeys, reassignmentAuditSupportedDataTypes } from "@/types/fieldReassignmentAudit"
import { FieldTypeId } from "@/types/fieldTypes"

const setup = namespace("setup")

@Component({
	components: {
		"v-select": vSelect,
		"scrollbar": VuePerfectScrollbar,
		"data-table": DataTable,
		"skeleton": SkeletonLoader,
		"field-control": FieldControl,
		"timezone-aliases": TimezoneAliases
	}
})
export default class TenantSettingsSetup extends Vue {
	@setup.Getter("getFieldTypes")
	private fieldTypes!: any[]

	@setup.Getter private getAccessibleFields!: CaseColumn[]
	@setup.Action private loadFieldsIfUnloaded!: () => Promise<Field[]>

	@Getter("getMe")
	public currentUser!: CurrentUser;

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	@Action("loadTenantSettings")
	private loadTenantSettings!: () => Promise<void>

	$refs!: {
		dataTable: DataTable
	};

	key: string = ""

	public items: Array<any> = []
	public settings: { [key: string]: any; } = {}

	public waiting: boolean = false

	public updates: { [ key: string]: any } = {}

	public get columns () {
		return [
			{
				key: "title",
				label: "Title"
			},
			{
				key: "description",
				label: "Description"
			},
			{
				key: "appropriateControl",
				label: "",
				hasTemplate: true
			}
		]
	}

	public get hasEditPermissions () {
		return this.currentUser.editData
	}

	public get hasUpdates (): boolean {
		return Object.keys(this.updates).length > 0
	}

	public async mounted () {
		await this.loadData()
	}

	private async loadData () {
		this.waiting = true
		const fieldLoadPromise = this.loadFieldsIfUnloaded()
		const timezonesPromise = api.timeZonesGet()
		const tenantSettingsPromise = apiTenant.getTenantSettings()

		await Promise.allSettled([fieldLoadPromise, timezonesPromise, tenantSettingsPromise])

		const timezones = await timezonesPromise
		const tenantSettings = await tenantSettingsPromise

		this.items = tenantSettings
			.filter(f => f.setting && f.setting.hideFromSetupPage !== true)
			.map(item => {
				const setting = {
					id: item.setting.key,
					title: item.setting.title,
					description: item.setting.description,
					dataType: item.setting.dataType,
					value: item.value,
					options: [] as any[],
					placeholder: null as (string | null),
					controlOptions: {}
				}

				if (setting.id === TenantSettingKeys.EnabledTimezones) {
					setting.options = timezones
					setting.placeholder = "All timezones allowed"
				}
				if (setting.id === TenantSettingKeys.DoNotDuplicateInsightOptionDefaults) {
					setting.options = insightDuplicationOptions
					setting.placeholder = "All options duplicated"
				}

				if (setting.id === TenantSettingKeys.TimezoneAliases) {
					setting.dataType = "timezone-alias"
					setting.options = timezones
					setting.placeholder = "All timezones allowed"
				}
				if (setting.id === TenantSettingKeys.TriageTemplateChange) {
					setting.options = TriageTemplateChangeSettingOptions
					setting.placeholder = "Select a Triage Template Change Setting"
					setting.controlOptions = { clearable: false }
				}

				if (setting.id === TenantSettingKeys.ReassignmentAuditFields) {
					setting.placeholder = "Select Fields available for reassignment auditing"
					if (this.getIsFeatureEnabled(TenantFeatures.FieldReassignmentAudit)) {
						fieldLoadPromise.then(() => setting.options = this.getAccessibleFields
							.filter(field =>
								(field.key.startsWith("fields.") || reassignmentAuditSupportedBuiltInCaseColumnKeys.includes(field.key)) &&
								reassignmentAuditSupportedDataTypes.includes(field.dataType as FieldTypeId)
							).map(field => ({ id: field.key, title: field.label })))
					}
				}

				return setting
			}).filter(this.requiredFeatureChecks)

		this.waiting = false

		this.updates = {}
		this.key = Guid.newGuid().toString()
	}

	private requiredFeatureChecks ({ id }: { id: string }): boolean {
		switch (id) {
			case TenantSettingKeys.TimezoneAliases:
				return this.getIsFeatureEnabled(TenantFeatures.TimezoneAliases)
			case TenantSettingKeys.TriageTemplateChange:
				return this.getIsFeatureEnabled(TenantFeatures.Triage)
			case TenantSettingKeys.ReassignmentAuditFields:
				return this.getIsFeatureEnabled(TenantFeatures.FieldReassignmentAudit)
			default:
				return true
		}
	}

	private showToast (message: string, title: string, variant: string) {
		this.$bvToast.toast(message, {
			title: title,
			variant: variant,
			solid: true,
			autoHideDelay: 5000
		})
	}

	public makeChange (key: string, value: any) {
		Vue.set(this.updates, key, value)
	}

	public async apply () {
		await apiTenant.saveTenantSettings(this.updates)
		this.updates = {}

		this.showToast("Some changes may require a page refresh to take effect", "General Settings Saved", "success")
		this.loadData()

		// Force refresh on tenant settings
		this.loadTenantSettings()
	}

	public revert () {
		this.loadData()
	}
}
